import {Component, Input} from '@angular/core';
import {formatDate} from '@angular/common';
import {Request} from '../../classes/request';
import {Customer} from '../../classes/customer';
import {DepreciationAsset} from '../../interfaces/depreciationAsset';
import {customerEmailAddresses} from '../../utils/customerEmails';

interface AnnexAssetView {
    description: string;
    rate: string;
    costDisplay: string;
    costCopy: string;
}

const REGION_BY_PROVINCE: Record<string, string> = {
    AG: 'Sicilia', AL: 'Piemonte', AN: 'Marche', AO: "Valle d'Aosta", AP: 'Marche', AQ: 'Abruzzo',
    AR: 'Toscana', AT: 'Piemonte', AV: 'Campania', BA: 'Puglia', BG: 'Lombardia', BI: 'Piemonte',
    BL: 'Veneto', BN: 'Campania', BO: 'Emilia-Romagna', BR: 'Puglia', BS: 'Lombardia', BT: 'Puglia',
    BZ: 'Trentino-Alto Adige', CA: 'Sardegna', CB: 'Molise', CE: 'Campania', CH: 'Abruzzo',
    CI: 'Sardegna', CL: 'Sicilia', CN: 'Piemonte', CO: 'Lombardia', CR: 'Lombardia', CS: 'Calabria',
    CT: 'Sicilia', CZ: 'Calabria', EN: 'Sicilia', FC: 'Emilia-Romagna', FE: 'Emilia-Romagna',
    FG: 'Puglia', FI: 'Toscana', FM: 'Marche', FR: 'Lazio', GE: 'Liguria', GO: 'Friuli-Venezia Giulia',
    GR: 'Toscana', IM: 'Liguria', IS: 'Molise', KR: 'Calabria', LC: 'Lombardia', LE: 'Puglia',
    LI: 'Toscana', LO: 'Lombardia', LT: 'Lazio', LU: 'Toscana', MB: 'Lombardia', MC: 'Marche',
    ME: 'Sicilia', MI: 'Lombardia', MN: 'Lombardia', MO: 'Emilia-Romagna', MS: 'Toscana',
    MT: 'Basilicata', NA: 'Campania', NO: 'Piemonte', NU: 'Sardegna', OG: 'Sardegna', OR: 'Sardegna',
    OT: 'Sardegna', PA: 'Sicilia', PC: 'Emilia-Romagna', PD: 'Veneto', PE: 'Abruzzo', PG: 'Umbria',
    PI: 'Toscana', PN: 'Friuli-Venezia Giulia', PO: 'Toscana', PR: 'Emilia-Romagna', PT: 'Toscana',
    PU: 'Marche', PV: 'Lombardia', PZ: 'Basilicata', RA: 'Emilia-Romagna', RC: 'Calabria',
    RE: 'Emilia-Romagna', RG: 'Sicilia', RI: 'Lazio', RM: 'Lazio', RN: 'Emilia-Romagna',
    RO: 'Veneto', SA: 'Campania', SI: 'Toscana', SO: 'Lombardia', SP: 'Liguria', SR: 'Sicilia',
    SS: 'Sardegna', SU: 'Sardegna', SV: 'Liguria', TA: 'Puglia', TE: 'Abruzzo', TN: 'Trentino-Alto Adige',
    TO: 'Piemonte', TP: 'Sicilia', TR: 'Umbria', TS: 'Friuli-Venezia Giulia', TV: 'Veneto',
    UD: 'Friuli-Venezia Giulia', VA: 'Lombardia', VB: 'Piemonte', VC: 'Piemonte', VE: 'Veneto',
    VI: 'Veneto', VR: 'Veneto', VS: 'Sardegna', VT: 'Lazio', VV: 'Calabria'
};

/**
 * Vista "Iperammortamento 4.0": replica la maschera GSE (punti 1-4) con i dati
 * della pratica in sola lettura. I campi senza un dato corrispondente restano vuoti.
 */
@Component({
    selector: 'app-request-iper-view',
    templateUrl: './request-iper-view.component.html',
    styleUrls: ['./request-iper-view.component.css']
})
export class RequestIperViewComponent {
    @Input() request: Request = new Request();

    readonly country = 'ITALIA';
    readonly completionHint = 'Data di effettuazione degli investimenti in beni materiali e immateriali nuovi strumentali all\'esercizio d\'impresa di cui agli allegati IV e V alla legge 30 dicembre 2025, n. 199, secondo le regole generali previste dai commi 1 e 2 dell\'articolo 109 del TUIR (prevista nella comunicazione preventiva e di conferma / effettiva nella comunicazione di completamento)';
    readonly interconnectionHint = 'Data prevista nella comunicazione preventiva e di conferma / effettiva nella comunicazione di completamento';
    readonly registrationHint = 'La data di iscrizione al Registro Imprese dev\'essere successiva alla data di costituzione e antecedente alla data odierna.';

    annexOpen: boolean[] = [];
    totalsIvOpen = true;
    totalsVOpen = true;

    private readonly amountFormatterSpace = (amount: number): string => {
        const [integer, decimals] = amount.toFixed(2).split('.');
        const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return `€ ${grouped},${decimals}`;
    };

    get customer(): Customer | null {
        return this.request?.customer ?? null;
    }

    get companyName(): string {
        return this.text(this.customer?.CompanyName);
    }

    get vatNumber(): string {
        return this.text(this.customer?.VATNumber);
    }

    /** Codice ATECO della pratica; in mancanza quello anagrafico del cliente. */
    get atecoCode(): string {
        return this.text(this.request?.AtecoCode) || this.text(this.customer?.AtecoCode);
    }

    get customerEmail(): string {
        return this.text(this.customer?.Email)
            || customerEmailAddresses(this.customer)[0]
            || this.text(this.request?.Email);
    }

    /**
     * CF del rappresentante: solo se il codice fiscale anagrafico è un CF persona
     * (16 caratteri). Il CF società a 11 cifre non va nel campo persona.
     */
    get signatoryFiscalCode(): string {
        const fiscalCode = this.text(this.customer?.FiscalCode).toUpperCase();
        return /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/i.test(fiscalCode)
            ? fiscalCode
            : '';
    }

    /** Provincia italiana a 2 lettere => nazione di nascita Italia. */
    get birthCountry(): string {
        const province = this.text(this.customer?.BirthPlaceProvince);
        return /^[A-Z]{2}$/i.test(province) ? this.country : '';
    }

    get birthPlace(): string {
        return this.place(this.customer?.BirthPlace, this.customer?.BirthPlaceProvince);
    }

    get legalCity(): string {
        return this.place(this.customer?.HeadOffice, this.customer?.HeadOfficeProvince);
    }

    /** Impresa autonoma: né società partecipata né soci partecipanti. */
    get isAutonomousCompany(): boolean {
        return this.isNo(this.request?.CompanyParticipation) && this.isNo(this.request?.PartnersParticipation);
    }

    /**
     * Appartenenza a un gruppo: sì se la pratica indica società partecipata o soci
     * partecipanti, no se entrambi sono negati.
     */
    get groupMembership(): string {
        if (this.isYes(this.request?.CompanyParticipation) || this.isYes(this.request?.PartnersParticipation)) {
            return 'SI';
        }
        return this.isAutonomousCompany ? 'NO' : '';
    }

    /** "Ha già un revisore legale dei conti" della pratica. */
    get statutoryAudit(): string {
        return this.yesNo(this.request?.TaxCredit5Revisor);
    }

    /**
     * Dimensione UE (raccomandazione 2003/361/CE) da ULA, fatturato e totale attivo.
     * Solo per l'impresa autonoma: per l'associata servirebbero i dati consolidati.
     */
    get companySize(): string {
        if (!this.isAutonomousCompany) {
            return '';
        }
        const employees = this.parseAmount(this.customer?.ULALastBalance);
        if (employees === null) {
            return '';
        }
        const turnover = this.parseAmount(this.customer?.SalesRevenues);
        const assets = this.parseAmount(this.customer?.TotalAssets);
        if (employees >= 250) {
            return 'Grande';
        }
        if (turnover === null && assets === null) {
            return '';
        }
        const within = (maxTurnover: number, maxAssets: number) =>
            (turnover !== null && turnover <= maxTurnover) || (assets !== null && assets <= maxAssets);
        if (employees < 10 && within(2_000_000, 2_000_000)) {
            return 'Micro';
        }
        if (employees < 50 && within(10_000_000, 10_000_000)) {
            return 'Piccola';
        }
        if (employees < 250 && within(50_000_000, 43_000_000)) {
            return 'Media';
        }
        return 'Grande';
    }

    get signatoryRole(): string {
        return this.text(this.customer?.Name) || this.text(this.customer?.Surname)
            ? 'Rappresentante Legale'
            : '';
    }

    get completionDate(): string {
        return this.date(this.request?.TaxCredit5EndDate);
    }

    get productionRegion(): string {
        const province = this.text(this.request?.OperationalHeadquartersProvince).toUpperCase();
        return REGION_BY_PROVINCE[province] || '';
    }

    get annexAssets(): AnnexAssetView[] {
        const assets = Array.isArray(this.request?.DepreciationAssets)
            ? this.request.DepreciationAssets
            : [];
        if (assets.length > 0) {
            const single = assets.length === 1;
            return assets.map(asset => this.toAnnexAsset(asset, single));
        }
        return [this.toAnnexAsset({
            description: '',
            rate: this.request?.DepreciationRate ?? null
        }, true)];
    }

    get annexTotalDisplay(): string {
        return this.moneyDisplay(this.investmentAmount);
    }

    get annexTotalCopy(): string {
        return this.moneyCopy(this.investmentAmount);
    }

    /** Nessun bene di allegato V in pratica: il totale di quel riquadro è zero. */
    get annexVTotalDisplay(): string {
        return this.moneyDisplay(0);
    }

    get annexVTotalCopy(): string {
        return this.moneyCopy(0);
    }

    isAnnexOpen(index: number): boolean {
        return this.annexOpen[index] !== false;
    }

    toggleAnnex(index: number): void {
        this.annexOpen[index] = !this.isAnnexOpen(index);
    }

    place(city?: string | null, province?: string | null): string {
        const name = this.text(city);
        const prov = this.text(province).toUpperCase();
        if (!name) {
            return '';
        }
        if (!prov || name.toUpperCase().includes(`(${prov})`)) {
            return name;
        }
        return `${name} (${prov})`;
    }

    date(value: Date | string | null | undefined): string {
        if (!value) {
            return '';
        }
        if (typeof value === 'string') {
            const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                return `${match[3]}-${match[2]}-${match[1]}`;
            }
        }
        try {
            return formatDate(value, 'dd-MM-yyyy', 'en-US');
        } catch {
            return '';
        }
    }

    private toAnnexAsset(asset: DepreciationAsset, withCost: boolean): AnnexAssetView {
        return {
            description: this.text(asset.description),
            rate: this.rateText(asset.rate),
            costDisplay: withCost ? this.annexTotalDisplay : '',
            costCopy: withCost ? this.annexTotalCopy : ''
        };
    }

    private get investmentAmount(): number | null {
        const raw = this.request?.InvestmentAmount;
        if (raw === null || raw === undefined || (raw as unknown) === '') {
            return null;
        }
        const parsed = Number(raw);
        return isNaN(parsed) ? null : parsed;
    }

    private moneyDisplay(amount: number | null): string {
        return amount === null ? '' : this.amountFormatterSpace(amount);
    }

    /** Formato da incollare: senza simbolo e senza separatore delle migliaia. */
    private moneyCopy(amount: number | null): string {
        return amount === null ? '' : amount.toFixed(2).replace('.', ',');
    }

    private rateText(rate: number | null | undefined): string {
        if (rate === null || rate === undefined || (rate as unknown) === '') {
            return '';
        }
        const parsed = Number(rate);
        if (isNaN(parsed)) {
            return '';
        }
        if (Number.isInteger(parsed)) {
            return String(parsed);
        }
        return new Intl.NumberFormat('it-IT', {maximumFractionDigits: 2}).format(parsed);
    }

    private parseAmount(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (typeof value === 'number') {
            return isNaN(value) ? null : value;
        }
        let normalized = value.trim().replace(/€/g, '').replace(/\s/g, '');
        if (!normalized) {
            return null;
        }
        const hasComma = normalized.includes(',');
        const hasDot = normalized.includes('.');
        if (hasComma && hasDot) {
            normalized = normalized.replace(/\./g, '').replace(',', '.');
        } else if (hasComma) {
            normalized = normalized.replace(',', '.');
        } else if (/^\d{1,3}(\.\d{3})+$/.test(normalized)) {
            normalized = normalized.replace(/\./g, '');
        }
        const parsed = Number(normalized);
        return isNaN(parsed) ? null : parsed;
    }

    private yesNo(value: boolean | number | null | undefined): string {
        if (this.isYes(value)) {
            return 'SI';
        }
        if (this.isNo(value)) {
            return 'NO';
        }
        return '';
    }

    private isYes(value: boolean | number | null | undefined): boolean {
        return value === true || value === 1;
    }

    private isNo(value: boolean | number | null | undefined): boolean {
        return value === false || value === 0;
    }

    private text(value: string | number | null | undefined): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim();
    }
}
